import { Link } from "react-router-dom"

import { publishBlockedReason } from "~/api/battery-presentation-errors"
import type { BatteryPresentationReadinessDto } from "~/api/dto/battery-presentation.dto"
import {
  useBatteryPresentationShare,
  useBatteryPresentationViews,
  usePublishBatteryPresentation,
  useUnpublishBatteryPresentation,
} from "~/api/hooks/useBatteryPresentationShare"
import type { ShareKindBundle } from "~/containers/ProposalSetup/share/share-kind-bundle"
import { ShareDrawer } from "~/containers/ProposalSetup/share/ShareDrawer"
import EnvVars from "~/env-vars"

import { batteryDrawerReturnTo } from "../financing-status/battery-drawer-launch"
import { RefreshPricingAction } from "../refresh-pricing-action"
import { BatterySavingsReportAction } from "../setup/BatterySavingsReportAction"
import { batteryProposalPath, batterySetupStepPath } from "../setup/setup-steps"

export {
  COPY_ERROR_PROMPT,
  COPY_LINK_LABEL,
  LINK_COPIED_LABEL,
  LOADING_SHARE_LABEL,
  OPEN_PRESENTATION_LABEL,
  PUBLIC_LINK_LABEL,
  PUBLISH_ERROR_PROMPT,
  PUBLISH_LABEL,
  PUBLISHING_LABEL,
  SHARE_DESCRIPTION,
  SHARE_LOAD_ERROR_PROMPT,
  SHARE_TITLE,
  UNPUBLISH_ERROR_PROMPT,
  UNPUBLISH_LABEL,
  UNPUBLISHING_LABEL,
  VIEW_ONLY_NOTE,
} from "~/containers/ProposalSetup/share/ShareDrawer"

export const NOT_CALCULATED_PROMPT = `Calculate System Pricing so this proposal has a saved financial summary before you share it.`
export const OUT_OF_DATE_PROMPT = `Pricing is out of date. Refresh Pricing so the homeowner sees current amounts.`
export const OPEN_SYSTEM_PRICING_LABEL = `Open System Pricing`
export const PUBLISHED_STALE_PROMPT = `Pricing is out of date, so the homeowner sees "Pricing is being updated" instead of amounts. Refresh Pricing to restore them.`

// Mirrors BatteryProposalUpdateGuard: the roles the server lets publish and
// unpublish. A read-only viewer sees the link, never a control that 403s.
const BATTERY_SHARE_UPDATE_PERMISSIONS = [
  `admin`,
  `vision_admin`,
  `vision_designer_user`,
  `vision_proposal_admin`,
  `proposal_creation`,
  `enterprise_consultant`,
  `mentorship_assignment`,
  `org_mentor_access`,
  `enterprise_admin`,
  `enterprise_admin_pro`,
  `enterprise_super_admin`,
] as const

type BatteryNotReady = Extract<BatteryPresentationReadinessDto, { ready: false }>

/**
 * Why publishing is unavailable, plus the one action that clears it. It also
 * runs beside an active link, where stale pricing is what the homeowner is
 * currently seeing rather than something blocking a publish.
 */
function BatteryNotReady({
  proposalId,
  readiness: { reason },
  published,
}: {
  proposalId: string
  readiness: BatteryNotReady
  published: boolean
}): JSX.Element {
  const prompt =
    reason === `not_calculated`
      ? NOT_CALCULATED_PROMPT
      : published
        ? PUBLISHED_STALE_PROMPT
        : OUT_OF_DATE_PROMPT

  return (
    <>
      <p role="status" className="text-[length:var(--step--1)] text-bp-gold">
        {prompt}
      </p>
      {reason === `out_of_date` ? (
        <RefreshPricingAction proposalId={proposalId} />
      ) : null}
      {/* System Pricing owns both answers, so it is the way out of either
          reason. An actor the server would refuse never reaches this drawer —
          the shell redirects them off /share — so Refresh Pricing above is
          always offered to someone who can use it. */}
      <Link
        to={batterySetupStepPath(proposalId, `system-pricing`)}
        className="text-[length:var(--step--2)] font-semibold text-bp-accent underline underline-offset-2 hover:text-bp-text"
      >
        {OPEN_SYSTEM_PRICING_LABEL}
      </Link>
    </>
  )
}

function BatteryShareExtraActions({
  proposalId,
}: {
  proposalId: string
}): JSX.Element | null {
  return EnvVars.BATTERY_SAVINGS_ENABLED ? (
    <BatterySavingsReportAction proposalId={proposalId} />
  ) : null
}

export const batteryShareBundle: ShareKindBundle<BatteryPresentationReadinessDto> =
  {
    testIdPrefix: `battery`,
    useShareState: useBatteryPresentationShare,
    useShareViews: useBatteryPresentationViews,
    usePublish: usePublishBatteryPresentation,
    useUnpublish: useUnpublishBatteryPresentation,
    blockedReadiness: (error) => {
      const reason = publishBlockedReason(error)
      return reason ? { ready: false, reason } : null
    },
    updatePermissions: BATTERY_SHARE_UPDATE_PERMISSIONS,
    NotReady: BatteryNotReady,
    ExtraActions: BatteryShareExtraActions,
    returnTo: (proposalId, state) =>
      batteryDrawerReturnTo(state, batteryProposalPath(proposalId)),
  }

/** Routed sibling of the Setup drawers at /battery-proposal/:proposalId/share. */
export const BatteryShareDrawer = (): JSX.Element | null => (
  <ShareDrawer bundle={batteryShareBundle} />
)
